import { Box } from "@chakra-ui/react";
import { customEvent, Field, Spacing } from "goi_common";
import { BaseButton, BaseStack, baseColors } from "goi_common/src/design-system";

/**
 * 장지 여유(`탐색`) 고객에게 상담 필요도를 묻는 단일 문항.
 *
 * 장례 견적의 `NotYetCustomerSurvey`(MatcRecommendationContainer)와 같은 패턴이지만
 * 문항과 라우팅이 다르다. 견적에서 `[장지 준비]` 는 병철에게 넘길 유의미한 니즈지만
 * 장지 퍼널에서는 자동 무효 대상이다. 컴포넌트를 공유하지 않는 이유다.
 *
 * 기획은 wiki/plans/jangji-relaxed-lead-triage.md 3-3.
 */

/**
 * 선택지 원문. **백엔드가 이 문자열을 substring 으로 매칭한다.**
 *
 *   `장례 비용` 또는 `시설 정보` 포함 -> 상담 (슬랙 스레드 + 상담전사들 태그,
 *                                        리드에서 무효 유형 제거)
 *   그 외                            -> 아무것도 하지 않는다 (무효 유지)
 *
 * 무효는 새로 찍는 것이 아니라 **리드 생성 시 붙은 무효 유형을 그대로 두는 것**이다.
 * 그래서 백엔드가 보는 키는 위 둘뿐이다 (`jangji_survey.CONSULT_KEYWORDS`).
 *
 * ⚠️ 표시 문구가 곧 매칭 키다. **문구를 고치면 위 키가 살아 있는지 반드시 확인할 것.**
 * 깨져도 화면은 멀쩡하고 슬랙 분기만 조용히 틀어진다.
 * 계약은 mumflower `wiki/api/jangji-recommend-request.md`.
 */
export const JANGJI_RELAXED_SURVEY_OPTIONS = [
  "전반적인 장례 비용, 절차 등도 함께 알아보고 있어요.",
  "장례식장, 화장장 등 시설 정보도 궁금해요.",
  "장지 계약만을 위해 알아보고 있어요.",
  "그냥 둘러보는 중이에요.",
];

/**
 * 백엔드 `jangji_survey.CONSULT_KEYWORDS` 와 같아야 한다.
 *
 * 상담시간 블록 노출도 이 키로 판정한다(`MatchRecommendationFacilityContainer`).
 * 프론트 판정이 백엔드와 어긋나면 상담 대상이 아닌 고객에게 콜백을 약속하게 되므로
 * 판정 문자열을 여기 한 곳에 둔다.
 */
export const BACKEND_MATCH_KEYS = ["장례 비용", "시설 정보"];

/**
 * 문구를 고치다 매칭 키를 날리면 화면은 멀쩡한 채로 슬랙 분기만 조용히 틀어진다.
 * 이 레포에 테스트 러너가 없어서 개발 환경 콘솔로 대신 알린다.
 */
if (process.env.NODE_ENV !== "production") {
  const missing = BACKEND_MATCH_KEYS.filter(
    (key) => !JANGJI_RELAXED_SURVEY_OPTIONS.some((option) => option.includes(key)),
  );
  if (missing.length > 0) {
    // eslint-disable-next-line no-console
    console.error(
      `[장지 여유 설문] 백엔드 매칭 키가 선택지에서 사라졌다: ${missing.join(", ")}. ` +
        `wiki/plans/jangji-relaxed-lead-triage.md 3-4 참고`,
    );
  }
}

interface JangjiRelaxedSurveyProps {
  answer: string;
  onAnswerChange: (answer: string) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
}

export function JangjiRelaxedSurvey({
  answer,
  onAnswerChange,
  onSubmit,
  isSubmitting = false,
}: JangjiRelaxedSurveyProps) {
  return (
    <BaseStack width="100%" alignItems="center" padding="40px 12px 12px">
      <BaseStack width="100%" maxWidth="480px">
        <Box className="title_3_b" color={baseColors.label_normal}>
          장지 준비, 하나씩 시작해보세요
        </Box>
        <Spacing gap={8} />
        <Box className="body_1_normal_m" color={baseColors.label_alternative_1}>
          견적서를 확인하기 전 마지막으로,
          <br />
          궁금하신 내용이 있다면 선택해주세요.
        </Box>

        <Spacing gap={32} />

        <Field.ButtonGroups
          title=""
          items={JANGJI_RELAXED_SURVEY_OPTIONS}
          value={answer}
          onChange={(selected) => {
            customEvent({
              newGtm: {
                path: "jangji_done",
                trigger_and_target: "srvy_opt",
                text: selected,
              },
              triggerHackle: true,
            });
            onAnswerChange(selected);
          }}
          borderColor={baseColors.line_normal}
          buttonWidth="100%"
        />

        <Spacing gap={32} />

        <BaseButton
          type="primary"
          size="large"
          rounded
          disabled={!answer || isSubmitting}
          onClick={onSubmit}
        >
          <Box className="body_1_normal_sb">견적서 확인하기</Box>
        </BaseButton>
      </BaseStack>
    </BaseStack>
  );
}
